import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { finalize } from 'rxjs';

import { ButtonComponent } from '../button/button.component';
import { ApiService } from '../../../core/services/api.service';
import { ApiEndPoints, ApiRoot } from '../../constants/api-endpoints';
import { Utils } from '../../../Utils/utils';
import { TextFieldComponent } from "../controls/text-field/text-field.component";
import { SkeletonLoaderComponent } from "../skeleton-loader/skeleton-loader.component";
import { SharedService } from '../../services/shared.service';
import { FieldValue } from '../../constants/services.enum';

@Component({
  standalone: true,
  selector: 'app-notes',
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, ButtonComponent, TextFieldComponent, SkeletonLoaderComponent],
  templateUrl: './notes.component.html',
  styleUrl: './notes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotesComponent implements OnChanges {
  fieldValue= FieldValue;
  @Input() srNumber: string = '';
  @Output() onClose = new EventEmitter();
  @Input() noteType = 'Note to Agent';
  @Input() allowSubmit: boolean = true;
  form: FormGroup;
  notes: any[] = [];
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  lang:string="en";
  constructor(
    private api: ApiService,
    private fb: FormBuilder,
    private sharedSevice: SharedService,
    private cdr: ChangeDetectorRef
  ) {
    this.lang = this.sharedSevice.getCurrentLang();
    this.form = this.fb.group({
      noteControl: [null, [Validators.required, Validators.maxLength(1000)]],
    })
    this.fetchNotes();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.fetchNotes();
  }


  // This method is used to submit a new note
  submitNote(): void {
    const noteControlValue = this.form.get('noteControl')?.value ?? '';
    const noteValue = String(noteControlValue).trim();
    if (!this.srNumber) {
      return;
    }
    if (!noteValue) {
      return;
    }

    if (this.form.get('noteControl')?.invalid || this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;
    this.cdr.markForCheck();
    let requestPayload = {
      "body": {
        "ProcessName": "DC Get Request Notes WF",
        "DCMethod": "SUBMIT",
        "IncomingHierarchy": {
          "MessageId": "123456",
          "IntObjectName": "DCSRNotesIO",
          "MessageType": "Integration Object",
          "IntObjectFormat": "Siebel Hierarchical",
          "ListOfDCSRNotesIO": {
            "Service Request": {
              "SR Number": this.srNumber,
              "ListOfFIN Service Request Notes": {
                "FIN Service Request Notes": [
                  {
                    "Note Type": this.lang == 'ar' ? FieldValue.NOTE_TYPE_AGENT_AR : FieldValue.NOTE_TYPE_AGENT,
                    "Note": noteValue
                  }
                ]
              }
            }
          }
        }
      }
    }
    this.api.post<any, any>(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.DCCI_NOTES,
      requestPayload
    ).pipe(
      finalize(() => {
        this.isSubmitting = false;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response: any) => {
        if (response?.SiebelMessage && response['Error Code'] == null) {
          this.onClose.emit();
          const control = this.form.get('noteControl');
          control?.setValue('');
          control?.markAsPristine();
          control?.markAsUntouched();
          this.cdr.markForCheck();
          this.fetchNotes();
        }
      },
      error: () => {
        // handle error silently for now
      }
    })
  }

  fetchNotes(): void {
    if (!this.srNumber) {
      return;
    }
    this.isLoading = true;
    this.cdr.markForCheck();
    let requestPayload = {
      "body": {
        "ProcessName": "DC Get Request Notes WF",
        "DCMethod": "QUERY",
        "IncomingHierarchy": {
          "MessageId": "123456",
          "IntObjectName": "DCSRNotesIO",
          "MessageType": "Integration Object",
          "IntObjectFormat": "Siebel Hierarchical",
          "ListOfDCSRNotesIO": {
            "Service Request": {
              "SR Number": this.srNumber
            }
          }
        }
      }
    }
    this.api.post<any, any>(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.DCCI_NOTES,
      requestPayload
    ).pipe(
      finalize(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response: any) => {
        const rawNotes = response?.SiebelMessage?.['Service Request']?.['FIN Service Request Notes'];
        this.notes = rawNotes ? Utils.checkIsArray(rawNotes) : [];
        this.cdr.markForCheck();
      },
      error: () => {
        this.notes = [];
        this.cdr.markForCheck();
      }
    })
  }
}
